import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft, Camera, Upload, Wand2, RotateCcw,
  Download, Save, Calculator, ChevronRight, Loader2, ZoomIn, FileText,
  Move, Check, ArrowRight
} from "lucide-react";
import { API } from "@/App";
import { generateQuotePDF } from "@/utils/generateQuotePDF";

// Physical pattern dimensions in cm
const PATTERN_W_CM = 600;
const PATTERN_H_CM = 290;

export default function SimulatorPage({ user, onLogout }) {
  const { wallpaperId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const selectorCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [wallpaper, setWallpaper] = useState(null);
  const [collection, setCollection] = useState(null);

  // Step control: 'dimensions' -> 'section' -> 'simulate' -> 'result'
  const [step, setStep] = useState("dimensions");

  // Step 1: dimensions
  const [wallWidth, setWallWidth] = useState("300");
  const [wallHeight, setWallHeight] = useState("270");

  // Step 2: section selector
  const [sectionX, setSectionX] = useState(0);
  const [sectionY, setSectionY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [patternImage, setPatternImage] = useState(null);
  const [croppedSection, setCroppedSection] = useState(null);

  // Step 3: simulate — overlay drag system
  const [roomImage, setRoomImage] = useState(null);
  const [method, setMethod] = useState("manual");
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [resultImage, setResultImage] = useState(null);
  const [resultBase64, setResultBase64] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Overlay state (wallpaper section on room photo)
  const containerRef = useRef(null);
  const [overlayPos, setOverlayPos] = useState({ x: 20, y: 20 });
  const [overlaySize, setOverlaySize] = useState({ w: 200, h: 180 });
  const [overlayDrag, setOverlayDrag] = useState(null); // null | 'move' | 'resize'
  const [overlayStart, setOverlayStart] = useState({ x: 0, y: 0, ox: 0, oy: 0, ow: 0, oh: 0 });
  const [overlayOpacity, setOverlayOpacity] = useState(0.8);

  useEffect(() => {
    fetchWallpaper();
  }, [wallpaperId]);

  const fetchWallpaper = async () => {
    try {
      const res = await axios.get(`${API}/api/wallpapers/${wallpaperId}`, { withCredentials: true });
      setWallpaper(res.data);
      setCollection(res.data.collection);
    } catch {
      toast.error("Carta da parati non trovata");
      navigate("/catalogo");
    }
  };

  const getProxiedUrl = (url) => {
    if (!url) return url;
    if (url.startsWith("data:")) return url;
    if (url.startsWith("/")) return `${API}${url}`;
    return `${API}/api/image-proxy?url=${encodeURIComponent(url)}`;
  };

  // ─── STEP 1 → 2: Load pattern and go to section selector ───
  const goToSectionStep = () => {
    const w = parseFloat(wallWidth) || 300;
    const h = parseFloat(wallHeight) || 270;
    if (w <= 0 || h <= 0 || w > 2000 || h > 2000) {
      toast.error("Inserisci dimensioni valide (1-2000 cm)");
      return;
    }
    // Reset section to center
    setSectionX(0);
    setSectionY(0);
    setPatternImage(null);
    setCroppedSection(null);
    setStep("section");

    // Load the wallpaper pattern image
    const textureUrl = wallpaper?.pattern_url || wallpaper?.image_url;
    if (textureUrl) {
      const img = new Image();
      const resolvedUrl = getProxiedUrl(textureUrl);
      // Only set crossOrigin for external URLs (proxied through /api/image-proxy)
      if (resolvedUrl.includes("/api/image-proxy")) {
        img.crossOrigin = "anonymous";
      }
      img.onload = () => setPatternImage(img);
      img.onerror = () => toast.error("Errore caricamento pattern");
      img.src = resolvedUrl;
    }
  };

  // ─── STEP 2: Section selector drawing ───
  useEffect(() => {
    if (step !== "section" || !patternImage) return;
    drawSectionSelector();
  }, [step, patternImage, sectionX, sectionY, wallWidth, wallHeight]);

  const drawSectionSelector = useCallback(() => {
    const canvas = selectorCanvasRef.current;
    if (!canvas || !patternImage) return;
    const ctx = canvas.getContext("2d");

    // Canvas display dimensions
    const displayW = canvas.parentElement?.clientWidth || 800;
    const aspectRatio = PATTERN_H_CM / PATTERN_W_CM;
    const displayH = displayW * aspectRatio;

    canvas.width = displayW;
    canvas.height = displayH;

    // Draw full pattern background
    ctx.drawImage(patternImage, 0, 0, displayW, displayH);

    // Darken the full image
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fillRect(0, 0, displayW, displayH);

    // Calculate selection rectangle in display coords
    const wCm = parseFloat(wallWidth) || 300;
    const hCm = parseFloat(wallHeight) || 270;
    const scaleX = displayW / PATTERN_W_CM;
    const scaleY = displayH / PATTERN_H_CM;

    const selW = Math.min(wCm * scaleX, displayW);
    const selH = Math.min(hCm * scaleY, displayH);
    const maxX = displayW - selW;
    const maxY = displayH - selH;
    const sx = Math.max(0, Math.min(sectionX, maxX));
    const sy = Math.max(0, Math.min(sectionY, maxY));

    // Draw bright selected area (clip and redraw pattern)
    ctx.save();
    ctx.beginPath();
    ctx.rect(sx, sy, selW, selH);
    ctx.clip();
    ctx.drawImage(patternImage, 0, 0, displayW, displayH);
    ctx.restore();

    // Selection border
    ctx.strokeStyle = "#d97706";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(sx, sy, selW, selH);
    ctx.setLineDash([]);

    // Corner handles
    const corners = [[sx, sy], [sx + selW, sy], [sx, sy + selH], [sx + selW, sy + selH]];
    corners.forEach(([cx, cy]) => {
      ctx.beginPath();
      ctx.arc(cx, cy, 5, 0, Math.PI * 2);
      ctx.fillStyle = "#d97706";
      ctx.fill();
      ctx.strokeStyle = "white";
      ctx.lineWidth = 1.5;
      ctx.stroke();
    });

    // Dimension label
    ctx.fillStyle = "rgba(217, 119, 6, 0.9)";
    const labelText = `${wCm} × ${hCm} cm`;
    ctx.font = "bold 13px sans-serif";
    const tm = ctx.measureText(labelText);
    const labelX = sx + selW / 2 - tm.width / 2 - 8;
    const labelY = sy + selH / 2 - 10;
    ctx.fillRect(labelX, labelY, tm.width + 16, 22);
    ctx.fillStyle = "white";
    ctx.textAlign = "center";
    ctx.fillText(labelText, sx + selW / 2, sy + selH / 2 + 4);
  }, [patternImage, sectionX, sectionY, wallWidth, wallHeight]);

  // Drag handlers for section selector
  const handleSelectorMouseDown = (e) => {
    const canvas = selectorCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    setDragging(true);
    setDragStart({
      x: e.clientX - rect.left - sectionX,
      y: e.clientY - rect.top - sectionY
    });
  };

  const handleSelectorMouseMove = useCallback((e) => {
    if (!dragging) return;
    const canvas = selectorCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const wCm = parseFloat(wallWidth) || 300;
    const hCm = parseFloat(wallHeight) || 270;
    const scaleX = canvas.width / PATTERN_W_CM;
    const scaleY = canvas.height / PATTERN_H_CM;
    const selW = Math.min(wCm * scaleX, canvas.width);
    const selH = Math.min(hCm * scaleY, canvas.height);
    const maxX = canvas.width - selW;
    const maxY = canvas.height - selH;

    const newX = Math.max(0, Math.min(e.clientX - rect.left - dragStart.x, maxX));
    const newY = Math.max(0, Math.min(e.clientY - rect.top - dragStart.y, maxY));
    setSectionX(newX);
    setSectionY(newY);
  }, [dragging, dragStart, wallWidth, wallHeight]);

  const handleSelectorMouseUp = () => setDragging(false);

  // Touch support for mobile
  const handleSelectorTouchStart = (e) => {
    e.preventDefault(); // Prevent iOS scroll/bounce
    const touch = e.touches[0];
    const canvas = selectorCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    setDragging(true);
    setDragStart({
      x: touch.clientX - rect.left - sectionX,
      y: touch.clientY - rect.top - sectionY
    });
  };

  const handleSelectorTouchMove = useCallback((e) => {
    if (!dragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    const canvas = selectorCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const wCm = parseFloat(wallWidth) || 300;
    const hCm = parseFloat(wallHeight) || 270;
    const scaleX = canvas.width / PATTERN_W_CM;
    const scaleY = canvas.height / PATTERN_H_CM;
    const selW = Math.min(wCm * scaleX, canvas.width);
    const selH = Math.min(hCm * scaleY, canvas.height);
    const maxX = canvas.width - selW;
    const maxY = canvas.height - selH;

    const newX = Math.max(0, Math.min(touch.clientX - rect.left - dragStart.x, maxX));
    const newY = Math.max(0, Math.min(touch.clientY - rect.top - dragStart.y, maxY));
    setSectionX(newX);
    setSectionY(newY);
  }, [dragging, dragStart, wallWidth, wallHeight]);

  useEffect(() => {
    window.addEventListener("mousemove", handleSelectorMouseMove);
    window.addEventListener("mouseup", handleSelectorMouseUp);
    window.addEventListener("touchmove", handleSelectorTouchMove, { passive: false });
    window.addEventListener("touchend", handleSelectorMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleSelectorMouseMove);
      window.removeEventListener("mouseup", handleSelectorMouseUp);
      window.removeEventListener("touchmove", handleSelectorTouchMove);
      window.removeEventListener("touchend", handleSelectorMouseUp);
    };
  }, [handleSelectorMouseMove, handleSelectorTouchMove]);

  // ─── STEP 2 → 3: Confirm section and crop ───
  const confirmSection = () => {
    if (!patternImage) return;
    const canvas = selectorCanvasRef.current;
    if (!canvas) return;

    const wCm = parseFloat(wallWidth) || 300;
    const hCm = parseFloat(wallHeight) || 270;
    const scaleX = canvas.width / PATTERN_W_CM;
    const scaleY = canvas.height / PATTERN_H_CM;
    const selW = Math.min(wCm * scaleX, canvas.width);
    const selH = Math.min(hCm * scaleY, canvas.height);
    const maxX = canvas.width - selW;
    const maxY = canvas.height - selH;
    const sx = Math.max(0, Math.min(sectionX, maxX));
    const sy = Math.max(0, Math.min(sectionY, maxY));

    // Map display coords back to source image coords
    const srcScaleX = patternImage.width / canvas.width;
    const srcScaleY = patternImage.height / canvas.height;
    const cropX = sx * srcScaleX;
    const cropY = sy * srcScaleY;
    const cropW = selW * srcScaleX;
    const cropH = selH * srcScaleY;

    // Create cropped canvas
    const cropCanvas = document.createElement("canvas");
    cropCanvas.width = Math.round(cropW);
    cropCanvas.height = Math.round(cropH);
    cropCanvas.getContext("2d").drawImage(
      patternImage, cropX, cropY, cropW, cropH,
      0, 0, cropCanvas.width, cropCanvas.height
    );
    setCroppedSection(cropCanvas);
    setStep("simulate");
  };

  // ─── STEP 3: Room image upload ───
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setRoomImage(ev.target.result);
      setResultImage(null);
      // Set initial overlay to ~60% of container centered
      setTimeout(() => {
        const cont = containerRef.current;
        if (cont) {
          const cw = cont.clientWidth;
          const ch = cont.clientHeight || 400;
          const ow = Math.round(cw * 0.5);
          const oh = Math.round(ow * ((parseFloat(wallHeight) || 270) / (parseFloat(wallWidth) || 300)));
          setOverlaySize({ w: ow, h: Math.min(oh, ch * 0.7) });
          setOverlayPos({ x: Math.round((cw - ow) / 2), y: Math.round((ch - Math.min(oh, ch * 0.7)) / 2) });
        }
      }, 100);
    };
    reader.readAsDataURL(file);
  };

  // ─── OVERLAY DRAG/RESIZE HANDLERS ───
  const getPointerPos = (e) => {
    if (e.touches) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  };

  const handleOverlayMoveStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getPointerPos(e);
    setOverlayDrag("move");
    setOverlayStart({ x: pos.x, y: pos.y, ox: overlayPos.x, oy: overlayPos.y, ow: overlaySize.w, oh: overlaySize.h });
  };

  const handleOverlayResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getPointerPos(e);
    setOverlayDrag("resize");
    setOverlayStart({ x: pos.x, y: pos.y, ox: overlayPos.x, oy: overlayPos.y, ow: overlaySize.w, oh: overlaySize.h });
  };

  const handleOverlayPointerMove = useCallback((e) => {
    if (!overlayDrag) return;
    const pos = e.touches ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };
    const dx = pos.x - overlayStart.x;
    const dy = pos.y - overlayStart.y;
    const cont = containerRef.current;
    const maxW = cont ? cont.clientWidth : 800;
    const maxH = cont ? cont.clientHeight : 600;

    if (overlayDrag === "move") {
      setOverlayPos({
        x: Math.max(0, Math.min(overlayStart.ox + dx, maxW - overlaySize.w)),
        y: Math.max(0, Math.min(overlayStart.oy + dy, maxH - overlaySize.h))
      });
    } else if (overlayDrag === "resize") {
      const newW = Math.max(60, Math.min(overlayStart.ow + dx, maxW - overlayPos.x));
      const aspect = (parseFloat(wallHeight) || 270) / (parseFloat(wallWidth) || 300);
      const newH = Math.round(newW * aspect);
      setOverlaySize({ w: newW, h: Math.min(newH, maxH - overlayPos.y) });
    }
  }, [overlayDrag, overlayStart, overlaySize.w, overlaySize.h, overlayPos.x, overlayPos.y, wallWidth, wallHeight]);

  const handleOverlayPointerUp = useCallback(() => {
    setOverlayDrag(null);
  }, []);

  useEffect(() => {
    if (overlayDrag) {
      window.addEventListener("mousemove", handleOverlayPointerMove);
      window.addEventListener("mouseup", handleOverlayPointerUp);
      window.addEventListener("touchmove", handleOverlayPointerMove, { passive: false });
      window.addEventListener("touchend", handleOverlayPointerUp);
      return () => {
        window.removeEventListener("mousemove", handleOverlayPointerMove);
        window.removeEventListener("mouseup", handleOverlayPointerUp);
        window.removeEventListener("touchmove", handleOverlayPointerMove);
        window.removeEventListener("touchend", handleOverlayPointerUp);
      };
    }
  }, [overlayDrag, handleOverlayPointerMove, handleOverlayPointerUp]);

  // ─── APPLY: Composite overlay onto room image ───
  const applyOverlay = () => {
    if (!croppedSection || !roomImage) return;
    setLoading(true);
    setMethod("manual");
    const cont = containerRef.current;
    if (!cont) return;

    const bgImg = new Image();
    bgImg.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) { setLoading(false); return; }

      // Map overlay position from display coords to image coords
      const displayW = cont.clientWidth;
      const displayH = cont.clientHeight;
      const scaleX = bgImg.width / displayW;
      const scaleY = bgImg.height / displayH;

      canvas.width = bgImg.width;
      canvas.height = bgImg.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bgImg, 0, 0);

      // Draw wallpaper section at the overlay position/size
      const dx = overlayPos.x * scaleX;
      const dy = overlayPos.y * scaleY;
      const dw = overlaySize.w * scaleX;
      const dh = overlaySize.h * scaleY;
      ctx.globalAlpha = overlayOpacity;
      ctx.drawImage(croppedSection, 0, 0, croppedSection.width, croppedSection.height, dx, dy, dw, dh);
      ctx.globalAlpha = 1;

      try {
        const dataUrl = canvas.toDataURL("image/png");
        setResultImage(dataUrl);
        setResultBase64(dataUrl.split(",")[1]);
        setStep("result");
      } catch {
        toast.error("Errore nell'esportazione. Riprova.");
      }
      setLoading(false);
    };
    bgImg.onerror = () => { setLoading(false); toast.error("Errore nel caricamento della foto."); };
    bgImg.src = roomImage;
  };

  const applyWallpaperAI = async () => {
    if (!roomImage || !wallpaper) return;
    setAiLoading(true);
    setMethod("ai");
    try {
      // Send the cropped section as the exact pattern to apply
      let sectionBase64 = null;
      if (croppedSection) {
        sectionBase64 = croppedSection.toDataURL("image/png").split(",")[1];
      }
      const res = await axios.post(`${API}/api/ai/apply-wallpaper`, {
        room_image_base64: roomImage.split(",")[1],
        wallpaper_id: wallpaperId,
        section_image_base64: sectionBase64,
        wall_width_cm: parseFloat(wallWidth) || 300,
        wall_height_cm: parseFloat(wallHeight) || 270,
      }, { withCredentials: true, timeout: 120000 });
      if (res.data.result_image_base64) {
        const dataUrl = `data:image/png;base64,${res.data.result_image_base64}`;
        setResultImage(dataUrl);
        setResultBase64(res.data.result_image_base64);
        setStep("result");
      }
    } catch {
      toast.error("Errore nella generazione AI. Usa la modalità manuale.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/simulations`, {
        wallpaper_id: wallpaperId,
        result_image_base64: resultBase64,
        wall_width_cm: parseFloat(wallWidth) || 300,
        wall_height_cm: parseFloat(wallHeight) || 270,
        method
      }, { withCredentials: true });
      toast.success("Simulazione salvata!");
      navigate("/storico");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    if (!resultImage) return;
    const link = document.createElement("a");
    link.download = `simulazione-${wallpaper?.name || "uol-paper"}.png`;
    link.href = resultImage;
    link.click();
  };

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      await generateQuotePDF({
        wallpaper, collection, resultImage,
        wallWidthCm: parseFloat(wallWidth) || 300,
        wallHeightCm: parseFloat(wallHeight) || 270,
        method, user,
      });
      toast.success("PDF preventivo scaricato!");
    } catch {
      toast.error("Errore nella generazione del PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const resetAll = () => {
    setStep("dimensions");
    setResultImage(null);
    setResultBase64(null);
    setRoomImage(null);
    setCroppedSection(null);
    setMethod("manual");
    setOverlayDrag(null);
  };

  // Calculations
  const wCm = parseFloat(wallWidth) || 300;
  const hCm = parseFloat(wallHeight) || 270;
  const areaSqm = (wCm / 100) * (hCm / 100);
  const pricePerSqm = collection?.price_per_sqm || 122;
  const totalPrice = (areaSqm * pricePerSqm).toFixed(2);
  const numPanelsX = Math.ceil(wCm / PATTERN_W_CM);
  const numPanelsY = Math.ceil(hCm / PATTERN_H_CM);

  if (!wallpaper) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-stone-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const stepLabels = [
    { key: "dimensions", num: "1", label: "Dimensioni" },
    { key: "section", num: "2", label: "Sezione" },
    { key: "simulate", num: "3", label: "Simulazione" },
  ];
  const currentStepIdx = stepLabels.findIndex(s => s.key === step || (step === "result" && s.key === "simulate"));

  return (
    <div className="min-h-screen bg-stone-50" data-testid="simulator-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/catalogo")} className="flex items-center gap-2 text-stone-500 hover:text-stone-900">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs uppercase tracking-widest hidden sm:inline">Catalogo</span>
            </button>
            <ChevronRight className="w-3 h-3 text-stone-300" />
            <span className="text-sm font-medium text-stone-900 truncate max-w-[200px]">{wallpaper.name}</span>
          </div>
          <span className="text-xs text-stone-400 uppercase tracking-widest">Simulatore</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {stepLabels.map((s, i) => (
            <div key={s.key} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                i <= currentStepIdx
                  ? "bg-stone-900 text-white"
                  : "bg-stone-200 text-stone-400"
              }`}>
                {i < currentStepIdx ? <Check className="w-4 h-4" /> : s.num}
              </div>
              <span className={`text-xs uppercase tracking-widest hidden sm:inline ${
                i <= currentStepIdx ? "text-stone-900" : "text-stone-400"
              }`}>{s.label}</span>
              {i < stepLabels.length - 1 && <div className={`w-8 sm:w-16 h-px ${i < currentStepIdx ? "bg-stone-900" : "bg-stone-200"}`} />}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main area */}
          <div className="lg:col-span-2">

            {/* STEP 1: Dimensions */}
            {step === "dimensions" && (
              <div className="bg-white border border-stone-200 p-8" data-testid="step-dimensions">
                <h2 className="text-xl font-light text-stone-900 mb-2">Inserisci le dimensioni della parete</h2>
                <p className="text-sm text-stone-500 mb-8">Misura la parete su cui applicare la carta da parati</p>
                <div className="max-w-md mx-auto">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-stone-500">Larghezza (cm)</Label>
                      <Input
                        data-testid="wall-width-input"
                        type="number"
                        value={wallWidth}
                        onChange={e => setWallWidth(e.target.value)}
                        className="rounded-none h-12 text-lg mt-2"
                        placeholder="300"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-stone-500">Altezza (cm)</Label>
                      <Input
                        data-testid="wall-height-input"
                        type="number"
                        value={wallHeight}
                        onChange={e => setWallHeight(e.target.value)}
                        className="rounded-none h-12 text-lg mt-2"
                        placeholder="270"
                      />
                    </div>
                  </div>
                  {/* Visual wall proportion */}
                  <div className="mb-6 flex justify-center">
                    <div
                      className="border-2 border-dashed border-stone-300 flex items-center justify-center text-xs text-stone-400"
                      style={{
                        width: `${Math.min(200, Math.max(60, (wCm / hCm) * 120))}px`,
                        height: `${Math.min(200, Math.max(60, (hCm / wCm) * 120))}px`,
                      }}
                    >
                      {wCm} × {hCm} cm
                    </div>
                  </div>
                  <Button
                    data-testid="next-step-btn"
                    onClick={goToSectionStep}
                    className="w-full rounded-none bg-stone-900 text-white hover:bg-stone-800 h-12 gap-2"
                  >
                    Scegli la sezione
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2: Section selector */}
            {step === "section" && (
              <div className="bg-white border border-stone-200 overflow-hidden" data-testid="step-section">
                <div className="p-3 border-b border-stone-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Move className="w-4 h-4 text-amber-600" />
                    <span className="text-xs uppercase tracking-widest text-stone-600">
                      Trascina per scegliere la sezione ({wCm}×{hCm} cm) del pattern ({PATTERN_W_CM}×{PATTERN_H_CM} cm)
                    </span>
                  </div>
                  <Button variant="ghost" onClick={() => setStep("dimensions")} className="rounded-none h-8 text-xs">
                    <ArrowLeft className="w-3 h-3 mr-1" /> Indietro
                  </Button>
                </div>
                <div className="relative select-none">
                  {patternImage ? (
                    <canvas
                      ref={selectorCanvasRef}
                      data-testid="section-canvas"
                      className="w-full cursor-move"
                      style={{ touchAction: "none", WebkitTouchCallout: "none", WebkitUserSelect: "none" }}
                      onMouseDown={handleSelectorMouseDown}
                      onTouchStart={handleSelectorTouchStart}
                    />
                  ) : (
                    <div className="h-48 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-stone-100 flex justify-end">
                  <Button
                    data-testid="confirm-section-btn"
                    onClick={confirmSection}
                    disabled={!patternImage}
                    className="rounded-none bg-amber-600 hover:bg-amber-700 text-white gap-2"
                  >
                    <Check className="w-4 h-4" /> Conferma sezione
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3: Simulate */}
            {step === "simulate" && (
              <div className="space-y-4">
                {/* Hidden canvas for compositing */}
                <canvas ref={canvasRef} className="hidden" />

                {/* Upload room photo */}
                {!roomImage ? (
                  <div className="bg-white border border-stone-200 p-8 min-h-[300px] flex flex-col items-center justify-center" data-testid="upload-area">
                    <h2 className="text-lg font-light text-stone-900 mb-2">Carica la foto della stanza</h2>
                    <p className="text-sm text-stone-500 mb-6">Scatta una foto o seleziona un'immagine dal dispositivo</p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <input type="file" ref={cameraInputRef} accept="image/*" capture="environment" onChange={handleFileUpload} className="hidden" />
                      <Button data-testid="camera-btn" onClick={() => cameraInputRef.current?.click()} variant="outline" className="rounded-none border-stone-300 h-12 px-6 gap-3">
                        <Camera className="w-5 h-5" /> Scatta Foto
                      </Button>
                      <input type="file" ref={fileInputRef} accept="image/*" onChange={handleFileUpload} className="hidden" />
                      <Button data-testid="upload-btn" onClick={() => fileInputRef.current?.click()} className="rounded-none bg-stone-900 text-white hover:bg-stone-800 h-12 px-6 gap-3">
                        <Upload className="w-5 h-5" /> Carica Immagine
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border border-stone-200 overflow-hidden">
                    <div className="p-3 border-b border-stone-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Move className="w-4 h-4 text-amber-600" />
                        <span className="text-xs uppercase tracking-widest text-stone-600">
                          Trascina e ridimensiona la carta sulla parete
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => { setRoomImage(null); setResultImage(null); }} className="rounded-none h-8 text-xs">
                          <RotateCcw className="w-3 h-3 mr-1" /> Cambia foto
                        </Button>
                      </div>
                    </div>

                    {/* Room image + draggable wallpaper overlay */}
                    <div
                      ref={containerRef}
                      className="relative select-none overflow-hidden"
                      style={{ touchAction: "none" }}
                      data-testid="overlay-container"
                    >
                      <img src={roomImage} alt="Stanza" className="w-full block" draggable={false} />

                      {/* Wallpaper overlay */}
                      {croppedSection && (
                        <div
                          data-testid="wallpaper-overlay"
                          className="absolute border-2 border-amber-500 shadow-lg"
                          style={{
                            left: overlayPos.x,
                            top: overlayPos.y,
                            width: overlaySize.w,
                            height: overlaySize.h,
                            opacity: overlayOpacity,
                            cursor: "move",
                            backgroundImage: `url(${croppedSection.toDataURL()})`,
                            backgroundSize: "100% 100%",
                            touchAction: "none",
                          }}
                          onMouseDown={handleOverlayMoveStart}
                          onTouchStart={handleOverlayMoveStart}
                        >
                          {/* Resize handle bottom-right */}
                          <div
                            data-testid="resize-handle"
                            className="absolute -bottom-2 -right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center cursor-se-resize shadow-md"
                            onMouseDown={handleOverlayResizeStart}
                            onTouchStart={handleOverlayResizeStart}
                          >
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="white">
                              <path d="M9 1L1 9M9 5L5 9M9 9L9 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          </div>
                          {/* Move indicator */}
                          <div className="absolute top-1 left-1 bg-black/50 rounded px-1.5 py-0.5 pointer-events-none">
                            <Move className="w-3 h-3 text-white" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Controls bar */}
                    <div className="p-3 border-t border-stone-100 flex flex-wrap items-center gap-3 justify-between">
                      <div className="flex items-center gap-2 text-xs text-stone-500">
                        <span>Opacità</span>
                        <input
                          type="range"
                          min="0.3"
                          max="1"
                          step="0.05"
                          value={overlayOpacity}
                          onChange={e => setOverlayOpacity(parseFloat(e.target.value))}
                          className="w-20 h-1 accent-amber-600"
                          data-testid="opacity-slider"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={applyWallpaperAI}
                          disabled={aiLoading}
                          className="rounded-none h-9 text-xs gap-1 border-amber-300 text-amber-700"
                        >
                          {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                          {aiLoading ? "AI..." : "Applica con AI"}
                        </Button>
                        <Button
                          data-testid="apply-overlay-btn"
                          onClick={applyOverlay}
                          disabled={loading}
                          className="rounded-none bg-amber-600 hover:bg-amber-700 text-white h-9 gap-1"
                        >
                          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Applica
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* RESULT */}
            {step === "result" && resultImage && (
              <div className="bg-white border border-stone-200 overflow-hidden">
                <div className="p-3 border-b border-stone-100 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-stone-600 flex items-center gap-2">
                    <ZoomIn className="w-4 h-4 text-green-600" />
                    Risultato simulazione ({method === "ai" ? "AI" : "Manuale"})
                  </span>
                  <Button data-testid="reset-btn" variant="ghost" onClick={resetAll} className="rounded-none h-8 text-xs gap-1">
                    <RotateCcw className="w-3 h-3" /> Nuova
                  </Button>
                </div>
                <img src={resultImage} alt="Risultato" className="w-full" style={{ objectFit: "contain" }} data-testid="result-image" />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Wallpaper info */}
            <div className="bg-white border border-stone-200 p-4">
              <div className="flex gap-3 mb-3">
                <div className="w-16 h-16 bg-stone-100 flex-shrink-0 overflow-hidden">
                  {wallpaper.image_url && (
                    <img src={wallpaper.image_url} alt={wallpaper.name} className="w-full h-full object-cover" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-stone-900">{wallpaper.name}</p>
                  <p className="text-xs text-stone-500">{collection?.name}</p>
                  <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 mt-1 inline-block ${
                    collection?.collection_type === "artist" ? "bg-amber-50 text-amber-700" : "bg-stone-100 text-stone-600"
                  }`}>
                    {collection?.collection_type === "artist" ? "Artist" : "Baseline"}
                  </span>
                </div>
              </div>
            </div>

            {/* Quote */}
            <div className="bg-white border border-stone-200 p-4">
              <h3 className="text-xs uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2">
                <Calculator className="w-3.5 h-3.5" />
                Preventivo
              </h3>
              <p className="text-[10px] text-stone-400 mb-3">Pattern: {PATTERN_W_CM} × {PATTERN_H_CM} cm</p>
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-stone-500">
                  <span>Parete</span>
                  <span>{wCm} × {hCm} cm</span>
                </div>
                <div className="flex justify-between text-xs text-stone-500">
                  <span>Area</span>
                  <span>{areaSqm.toFixed(2)} mq</span>
                </div>
                <div className="flex justify-between text-xs text-stone-500">
                  <span>Teli necessari</span>
                  <span>{numPanelsX * numPanelsY} ({numPanelsX}×{numPanelsY})</span>
                </div>
                <div className="flex justify-between text-xs text-stone-500">
                  <span>Prezzo/mq</span>
                  <span>{pricePerSqm.toFixed(2).replace('.', ',')} €</span>
                </div>
                <div className="flex justify-between text-base font-medium text-stone-900 pt-2 border-t border-stone-100">
                  <span>Totale (no posa)</span>
                  <span data-testid="total-price">{totalPrice.replace('.', ',')} €</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            {step === "result" && resultImage && (
              <div className="bg-white border border-stone-200 p-4 space-y-3">
                <Button data-testid="download-pdf-btn" onClick={handleDownloadPDF} disabled={pdfLoading} className="w-full rounded-none bg-amber-600 hover:bg-amber-700 text-white h-11 gap-2">
                  {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {pdfLoading ? "Generazione PDF..." : "Scarica PDF Preventivo"}
                </Button>
                <Button data-testid="save-simulation-btn" onClick={handleSave} disabled={saving} className="w-full rounded-none bg-stone-900 text-white hover:bg-stone-800 h-11 gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? "Salvataggio..." : "Salva Simulazione"}
                </Button>
                <Button data-testid="download-btn" onClick={handleDownload} variant="outline" className="w-full rounded-none h-11 gap-2 border-stone-200">
                  <Download className="w-4 h-4" /> Scarica Immagine
                </Button>
              </div>
            )}

            {/* Instructions */}
            {step !== "result" && (
              <div className="bg-amber-50 border border-amber-200 p-4">
                <h3 className="text-xs uppercase tracking-widest text-amber-800 mb-2">Istruzioni</h3>
                <ol className="text-xs text-amber-700 space-y-1.5 list-decimal list-inside">
                  {step === "dimensions" && (
                    <>
                      <li>Inserisci larghezza e altezza della parete in cm</li>
                      <li>Clicca "Scegli la sezione" per proseguire</li>
                    </>
                  )}
                  {step === "section" && (
                    <>
                      <li>Vedi il pattern completo {PATTERN_W_CM}×{PATTERN_H_CM} cm</li>
                      <li>Trascina il riquadro per scegliere la sezione che preferisci</li>
                      <li>Conferma la selezione per procedere</li>
                    </>
                  )}
                  {step === "simulate" && (
                    <>
                      <li>Carica o scatta una foto della stanza</li>
                      <li>Trascina la carta sulla parete, ridimensiona dall'angolo</li>
                      <li>Regola l'opacità per vedere meglio la posizione</li>
                      <li>Premi "Applica" per confermare o usa l'AI</li>
                    </>
                  )}
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
